/**
 * Erreur au retour d'une connexion Google / Facebook / Apple sur le web.
 *
 * Quand l'échange avec le fournisseur échoue, Supabase renvoie vers l'app avec
 * `?error=…&error_description=…` (ou dans le fragment `#`). Sans ce module,
 * l'app redirigeait aussitôt vers la page de connexion et l'utilisateur ne
 * voyait RIEN — cas réel (11/10/2026) : secret Apple expiré, chaque « Se
 * connecter avec Apple » revenait silencieusement à l'écran de connexion.
 *
 * `captureOAuthReturnError()` (main.tsx, au démarrage) garde le message le temps
 * de la redirection et nettoie l'adresse ; le formulaire de connexion l'affiche
 * (`peekOAuthReturnError` puis `clearOAuthReturnError`).
 */

const KEY = 'jr_oauth_error';

/** paramètres d'erreur OAuth à retirer de l'adresse */
const ERROR_PARAMS = ['error', 'error_code', 'error_description'];

/**
 * Message à montrer, à partir de la query et du fragment de l'adresse de retour.
 * `null` : pas d'erreur, ou annulation par l'utilisateur (rien à signaler).
 */
export function parseOAuthReturnError(search: string, hash: string): string | null {
  const read = (s: string) => new URLSearchParams(s.replace(/^[?#]/, ''));
  const q = read(search);
  const h = read(hash);
  const error = q.get('error') ?? h.get('error');
  const description = q.get('error_description') ?? h.get('error_description');
  if (!error && !description) return null;
  // l'utilisateur a fermé la fenêtre du fournisseur : c'est son choix
  if (error === 'access_denied' || /cancel/i.test(description ?? '')) return null;
  return 'La connexion avec ce compte n’a pas abouti. Réessaie dans un instant, ou connecte-toi avec ton adresse email.';
}

/** Adresse sans les paramètres d'erreur OAuth (query et fragment). */
export function stripOAuthErrorParams(href: string): string {
  const url = new URL(href);
  ERROR_PARAMS.forEach((p) => url.searchParams.delete(p));
  if (url.hash) {
    const h = new URLSearchParams(url.hash.slice(1));
    if (ERROR_PARAMS.some((p) => h.has(p))) {
      ERROR_PARAMS.forEach((p) => h.delete(p));
      const rest = h.toString();
      url.hash = rest ? '#' + rest : '';
    }
  }
  return url.toString();
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Au démarrage : garde le message et nettoie l'adresse. */
export function captureOAuthReturnError(): void {
  if (typeof window === 'undefined') return;
  const { search, hash, href } = window.location;
  if (!/error/.test(search + hash)) return;
  const message = parseOAuthReturnError(search, hash);
  try {
    if (message) storage()?.setItem(KEY, message);
    window.history.replaceState(window.history.state, '', stripOAuthErrorParams(href));
  } catch {
    // stockage ou historique indisponibles : on n'empêche jamais le démarrage
  }
}

export function peekOAuthReturnError(): string | null {
  try {
    return storage()?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
}

export function clearOAuthReturnError(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // rien
  }
}
